import type {
  ORGANIZATION_SETTINGS_TAB_VALUES,
  PROFILE_SETTINGS_TAB_VALUES,
} from "../constants/navigation-tabs";
import type {
  IGlobalSearchStaticEntry,
  IGlobalSearchTarget,
} from "./global-search";

export type TProfileSettingsTabValue =
  (typeof PROFILE_SETTINGS_TAB_VALUES)[keyof typeof PROFILE_SETTINGS_TAB_VALUES];

export type TOrganizationSettingsTabValue =
  (typeof ORGANIZATION_SETTINGS_TAB_VALUES)[keyof typeof ORGANIZATION_SETTINGS_TAB_VALUES];

// NOTE: NSAI-6588 — every visibility condition an entry of the static global
// search index (GLOBAL_SEARCH_STATIC_ENTRIES) can carry. An entry is offered
// only when EVERY gate on it is open (`entry.gates.every(isGateOpen)`), so an
// OR-condition is a gate of its own rather than two gates.
//
// Each platform implements one TGlobalSearchGateResolver answering these from
// the SAME state its navigation already reads — the hooks named in each note —
// so the palette offers an entry exactly when the page / tab / control it opens
// would render. "Web" paths are under apps/nsai/web/src/, "mobile" paths under
// apps/nsai/mobile/src/ (routes under apps/nsai/mobile/app/(app)/).
//
// Conventions:
// - Permissions are those of the ACTIVE project (usePermissionCheck).
// - ORG_* gates are about the organization the ACTIVE project belongs to. An
//   organization SETTINGS target carries no `organizationUuid`: the platform
//   fills in the active project's organization when it navigates. Every
//   organization-settings entry also carries IN_ORGANIZATION, so a personal
//   project never offers one.
// - A gate that still waits on /app-config should answer "closed": the palette
//   must never offer a destination the page will then redirect away from.
// - Gates marked "PLATFORM-SPECIFIC" are answered differently by web and mobile
//   because their navigation genuinely differs; each note names both sources.
export enum EGlobalSearchGate {
  // NOTE: True only in the web app (and the browser extension, which opens
  // web). For entries whose destination has no mobile screen.
  PLATFORM_WEB = "platform_web",
  // NOTE: True only in the mobile app. For entries whose destination has no
  // web screen.
  PLATFORM_MOBILE = "platform_mobile",

  // NOTE: The active project belongs to an organization (!isPersonalProject
  // from useAuth). Web: routes/index.tsx:319 (Analytics) and the organization
  // settings shell; mobile: the same flag from its auth context.
  IN_ORGANIZATION = "in_organization",
  // NOTE: The user owns the active project's organization
  // (currentOrganization.isOwner). Web: settings/organization/details/
  // index.tsx:97 (ownerOnlyTabs) and :351-410; mobile: screens/organizations/
  // components/OrganizationDetails.tsx:243-331.
  ORG_OWNER = "org_owner",

  // NOTE: private_assistant:read OR public_assistant:read.
  // Web: routes/index.tsx:130 + :205.
  ASSISTANT_READ = "assistant_read",
  // NOTE: knowledge:read. Web: routes/index.tsx:230.
  KNOWLEDGE_READ = "knowledge_read",
  // NOTE: model:read. Web: routes/index.tsx:200; mobile: models/index.tsx:18.
  MODEL_READ = "model_read",
  // NOTE: prompt_library:read. Web: routes/index.tsx:314.
  PROMPT_LIBRARY_READ = "prompt_library_read",
  // NOTE: chat_share:read — the Profile › Shared chats tab. Web: settings/
  // profile/index.tsx; mobile: screens/profile-settings/ProfileSettings.tsx.
  CHAT_SHARE_READ = "chat_share_read",
  // NOTE: all_members_analytics:read OR analytics:read OR probe:read — the
  // Security Analytics page itself (combine with IN_ORGANIZATION).
  // Web: routes/index.tsx:319-324; mobile: analytics/index.tsx.
  ANALYTICS_PAGE = "analytics_page",
  // NOTE: all_members_analytics:read OR analytics:read — the Dashboard and
  // Messages analytics tabs (pages/analytics/Analytics.tsx:49) and the
  // personal Token Usage / Token & Cost Usage tabs (settings/profile/
  // index.tsx:52,66). Mobile's personal Token Usage tab does not check it
  // (screens/profile-settings/ProfileSettings.tsx:110); web is canonical.
  ANALYTICS_READ = "analytics_read",
  // NOTE: probe:read — the Probes analytics tab. Web: pages/analytics/
  // Analytics.tsx:56; mobile: screens/analytics/AnalyticsScreen.tsx:65.
  PROBE_READ = "probe_read",
  // NOTE: appConfig.features.isAgentsEnabled (useAgentsEnabled).
  // Web: routes/index.tsx:210; mobile: agents/index.tsx:17.
  AGENTS_ENABLED = "agents_enabled",
  // NOTE: PLATFORM-SPECIFIC. Web: automation:read (routes/index.tsx:210 — the
  // route, stricter than the sidebar at layout/MainSidebar.tsx:461). Mobile:
  // automation:read OR automation_manager:read (agents/index.tsx:26).
  AGENTS_READ = "agents_read",
  // NOTE: marketplace_automation:read — the Agents "Marketplace" tab.
  // Web: pages/agents/Agents.tsx:957.
  MARKETPLACE_AUTOMATION_READ = "marketplace_automation_read",
  // NOTE: useAnonymizationLabEnabled (app-config flag); the route has no
  // permission check. Web: routes/index.tsx:150,215.
  ANONYMIZATION_LAB_ENABLED = "anonymization_lab_enabled",
  // NOTE: useDocRecognitionEnabled (app-config flag). Web: routes/index.tsx:158
  // and settings/organization/details/index.tsx:379.
  DOC_RECOGNITION_ENABLED = "doc_recognition_enabled",
  // NOTE: doc_recognition:read. Web: routes/index.tsx:158.
  DOC_RECOGNITION_READ = "doc_recognition_read",
  // NOTE: useSkillsEnabled (app-config flag). Web: routes/index.tsx:174.
  SKILLS_ENABLED = "skills_enabled",
  // NOTE: useSkillPermissions().canReadAny (private OR public skill read).
  // Web: routes/index.tsx:174, hooks/use-skill-permissions.ts:83.
  SKILL_READ = "skill_read",
  // NOTE: useSkillPermissions().canReadOrgCatalog (organization_skill:read).
  // Web: routes/index.tsx:180.
  SKILL_CATALOG_READ = "skill_catalog_read",
  // NOTE: The Integrations page: integration:read OR (custom_integration:read
  // AND the custom-integration flag). Web: routes/index.tsx:290-293; mobile:
  // integrations/index.tsx:26.
  INTEGRATIONS_PAGE = "integrations_page",
  // NOTE: integration:read — the "All" and "Connected" Integrations tabs.
  // Web: pages/integrations/Integrations.tsx:220.
  INTEGRATION_READ = "integration_read",
  // NOTE: The "Custom" Integrations tab: custom-integration flag AND any of
  // custom_integration read / write / delete. Web: pages/integrations/
  // Integrations.tsx:76; mobile: hooks/use-custom-integration-access.
  CUSTOM_INTEGRATION_ACCESS = "custom_integration_access",
  // NOTE: useCallAssistantFeaturesEnabled (app-config flag). Web:
  // routes/index.tsx:84 (pages), settings/profile/index.tsx:73 (Calendars tab),
  // profile/tabs/preferences/PreferencesSetting.tsx:39 (auto join, email
  // recap), settings/organization/details/index.tsx:395 (org tab).
  CALL_ASSISTANT_ENABLED = "call_assistant_enabled",
  // NOTE: call_assistant:read (useCallAssistantPermissions().canRead).
  // Web: routes/index.tsx:93; profile/tabs/calendar-setting/CalendarSetting.tsx:10.
  CALL_ASSISTANT_READ = "call_assistant_read",

  // NOTE: Personal Billing: !isOnPremise AND !isBillingDisabled.
  // Web: settings/profile/index.tsx:72. Mobile has no billing screen.
  BILLING_AVAILABLE = "billing_available",
  // NOTE: The Delete account section. PLATFORM-SPECIFIC — web: !isOnPremise
  // (profile/tabs/general-information/GeneralInformation.tsx:375); mobile: the
  // tenant's policies.isAccountDeletionEnabled (NSAI-5884).
  ACCOUNT_DELETION_AVAILABLE = "account_deletion_available",
  // NOTE: The user has an email + password sign-in (user.provider includes
  // EMAIL), so the password fields exist. Web: GeneralInformation.tsx:265.
  PASSWORD_SIGN_IN = "password_sign_in",
  // NOTE: useExternalApiEnabled. Web: settings/profile/index.tsx:147 and
  // settings/organization/details/index.tsx:356.
  EXTERNAL_API_ENABLED = "external_api_enabled",
  // NOTE: useUserMemoryEnabled (soft-launch flag). Web: settings/profile/
  // index.tsx:85.
  USER_MEMORY_ENABLED = "user_memory_enabled",
  // NOTE: useBotSettingsEnabled — slackBotUri OR teamsBotUri OR
  // isMatrixBotEnabled, and on web also isTelegramBotEnabled (the Telegram
  // tab is web only, NSAI-3874). Web: settings/profile/index.tsx:81,
  // hooks/use-bot-settings-enabled.ts.
  BOT_SETTINGS_ENABLED = "bot_settings_enabled",
  // NOTE: appConfig.bots.slackBotUri is set. Web: profile/tabs/bot-settings/
  // BotSettings.tsx:38.
  SLACK_BOT_ENABLED = "slack_bot_enabled",
  // NOTE: appConfig.bots.teamsBotUri is set. Web: BotSettings.tsx:39.
  TEAMS_BOT_ENABLED = "teams_bot_enabled",
  // NOTE: appConfig.bots.isMatrixBotEnabled. Web: BotSettings.tsx:42.
  MATRIX_BOT_ENABLED = "matrix_bot_enabled",
  // NOTE: appConfig.bots.isTelegramBotEnabled. Web: profile/tabs/bot-settings/
  // BotSettings.tsx (the Telegram tab).
  TELEGRAM_BOT_ENABLED = "telegram_bot_enabled",
  // NOTE: useFeatureFlag(EFeatureFlag.WHATSAPP_BOT). Web: profile/tabs/
  // general-information/WhatsappBotSection.tsx:27.
  WHATSAPP_BOT_ENABLED = "whatsapp_bot_enabled",
  // NOTE: useTokenUsageEnabled. Web: settings/profile/index.tsx:52,
  // settings/organization/details/index.tsx:400.
  TOKEN_USAGE_ENABLED = "token_usage_enabled",
  // NOTE: useTokenCostUsageEnabled. Web: settings/profile/index.tsx:66,
  // settings/organization/details/index.tsx:403, and the spending limits in
  // OrganizationUsageLimits/UsageLimitsForm.tsx:189.
  TOKEN_COST_USAGE_ENABLED = "token_cost_usage_enabled",
  // NOTE: NSAI-8010 — useUserAnalyticsEnabled. Mobile: the User Analytics tab
  // in OrganizationDetails.tsx; web shows it as a Token Usage pane.
  USER_ANALYTICS_ENABLED = "user_analytics_enabled",
  // NOTE: useTokenLimitsEnabled. Web: settings/organization/details/
  // index.tsx:406.
  TOKEN_LIMITS_ENABLED = "token_limits_enabled",
  // NOTE: TTS is on AND more than one voice is configured (a single voice
  // leaves nothing to choose). Web: PreferencesSetting.tsx:92.
  TTS_VOICE_CHOICE = "tts_voice_choice",
  // NOTE: PLATFORM-SPECIFIC. Web: always true (PreferencesSetting.tsx:178).
  // Mobile: the active tenant is not on-premise, from useDeployment()
  // (screens/profile-settings/components/Preferences.tsx:51).
  THEME_PICKER = "theme_picker",

  // NOTE: Owner OR any org project grants canReadProject. Web: settings/
  // organization/details/index.tsx:237.
  ORG_PROJECT_READ = "org_project_read",
  // NOTE: Owner OR any org project grants canReadProjectRole. Web:
  // settings/organization/details/index.tsx:242.
  ORG_ROLE_READ = "org_role_read",
  // NOTE: Owner OR any org project grants canWriteProjectRole. Web:
  // settings/organization/details/OrganizationRoles/index.tsx:76.
  ORG_ROLE_WRITE = "org_role_write",
  // NOTE: Owner OR any org project grants canReadApiKeys. Web: settings/
  // organization/details/index.tsx:247.
  ORG_API_KEY_READ = "org_api_key_read",
  // NOTE: Owner OR any org project grants canInviteMembersToProject. Web:
  // settings/organization/details/OrganizationMembers.tsx:432.
  ORG_MEMBER_INVITE = "org_member_invite",
  // NOTE: Owner OR any org project grants canReadUsageLimits. Web: settings/
  // organization/details/index.tsx:254.
  ORG_USAGE_LIMITS_READ = "org_usage_limits_read",
  // NOTE: getSiemConfigAccess(...).canRead (shared util). Web: settings/
  // organization/details/index.tsx:264.
  ORG_SIEM_CONFIG_READ = "org_siem_config_read",
  // NOTE: getMemoryPolicyAccess(...).canRead (shared util). Web: settings/
  // organization/details/index.tsx:277.
  ORG_MEMORY_POLICY_READ = "org_memory_policy_read",
  // NOTE: NSAI-7442 — getExternalApiLimitsAccess(...).canRead (shared util).
  // Web: settings/organization/details/index.tsx.
  ORG_EXTERNAL_API_LIMITS_READ = "org_external_api_limits_read",
  // NOTE: PLATFORM-SPECIFIC. The deployment enables at least one provider with
  // a section on the organization Integration tab (or the enabled list has not
  // resolved). Web: WEB_ORGANIZATION_CONFIGURABLE_PROVIDERS, settings/
  // organization/details/index.tsx:217. Mobile:
  // MOBILE_ORGANIZATION_CONFIGURABLE_PROVIDERS, OrganizationDetails.tsx:152.
  ORG_CONFIGURABLE_INTEGRATION = "org_configurable_integration",
  // NOTE: PLATFORM-SPECIFIC — the organization Appearance tab exists. Web:
  // always true (owner-only via ORG_OWNER). Mobile: isBrandedPresentationsEnabled
  // (OrganizationDetails.tsx:309), the presentation theme being the only
  // appearance surface mobile has.
  ORG_APPEARANCE_AVAILABLE = "org_appearance_available",
  // NOTE: useSyncatorApiEnabled. Web: settings/organization/details/
  // index.tsx:374.
  SYNCATOR_API_ENABLED = "syncator_api_enabled",
  // NOTE: useSiemExportEnabled. Web: settings/organization/details/
  // index.tsx:388.
  SIEM_EXPORT_ENABLED = "siem_export_enabled",
  // NOTE: useBrandedPresentationsEnabled. Web: components/appearance/
  // AppearanceContent.tsx:603.
  BRANDED_PRESENTATIONS_ENABLED = "branded_presentations_enabled",
  // NOTE: useFinancialMaskingFeatureEnabled. Web: settings/organization/
  // details/ChatSettings/index.tsx:202.
  FINANCIAL_MASKING_ENABLED = "financial_masking_enabled",
  // NOTE: usePiiCustomEntitiesFeatureEnabled — the custom entities section.
  // Web: ChatSettings/index.tsx.
  PII_CUSTOM_ENTITIES_ENABLED = "pii_custom_entities_enabled",
  // NOTE: NSAI-6787 — usePiiAdvancedRulesFeatureEnabled, the "PII detection
  // rules (advanced)" section. Web: ChatSettings/index.tsx.
  PII_ADVANCED_RULES_ENABLED = "pii_advanced_rules_enabled",
  // NOTE: usePiiIntentRoutingFeatureEnabled — the routing toggle and its two
  // model slots. Web: ChatSettings/index.tsx:187,287.
  PII_INTENT_ROUTING_ENABLED = "pii_intent_routing_enabled",
  // NOTE: useSamlSeatTiersEnabled. Web: settings/organization/details/
  // OrganizationSaml.tsx:462.
  SAML_SEAT_TIERS_ENABLED = "saml_seat_tiers_enabled",
  // NOTE: useDomainVerificationAvailable (false on a closed-network
  // deployment). Web: OrganizationSaml.tsx:484.
  DOMAIN_VERIFICATION_AVAILABLE = "domain_verification_available",
  // NOTE: The per-model request limits endpoint reports `isEnabled: true`.
  // Web: settings/organization/details/OrganizationUsageLimits/
  // ModelTokenLimits/index.tsx:44.
  MODEL_TOKEN_LIMITS_ENABLED = "model_token_limits_enabled",
  // NOTE: One gate per provider section on the organization Integration tab —
  // the enabled integrations list (useGetEnabledIntegrations) includes that
  // EIntegrationProvider value. Web: settings/organization/details/
  // OrganizationIntegration.tsx:72-107.
  INTEGRATION_BITRIX24_ENABLED = "integration_bitrix24_enabled",
  INTEGRATION_AZURE_MES_ENABLED = "integration_azure_mes_enabled",
  INTEGRATION_KONTUR_FOCUS_ENABLED = "integration_kontur_focus_enabled",
  INTEGRATION_KONTUR_DIADOC_ENABLED = "integration_kontur_diadoc_enabled",
  INTEGRATION_JIRA_DC_ENABLED = "integration_jira_dc_enabled",
  INTEGRATION_CONFLUENCE_DC_ENABLED = "integration_confluence_dc_enabled",
  INTEGRATION_NEXTCLOUD_ENABLED = "integration_nextcloud_enabled",
  INTEGRATION_MCPS_ENABLED = "integration_mcps_enabled",

  // NOTE: NSAI-7414 — the server serves the API behind the destination. One
  // gate per EServerApiCapability value an index entry needs. Web: always
  // true, since the web app is served by its own server. Mobile: the active
  // tenant's deployment.apiCapabilities (useServerApiCapabilities), which an
  // older server does not send.
  API_MEMORY_POLICY = "api_memory_policy",
  API_PROBE_MODELS = "api_probe_models",
  API_SUMMARY_MODEL = "api_summary_model",
  API_PROMPT_IMPROVEMENT_MODEL = "api_prompt_improvement_model",
  API_SEARCH_EMBEDDING_MODEL = "api_search_embedding_model",
  API_CONNECTOR_APPROVAL = "api_connector_approval",
  API_DATA_RETENTION = "api_data_retention",
  API_CUSTOM_INSTRUCTIONS = "api_custom_instructions",
  API_AI_GENERATED_FILES = "api_ai_generated_files",
  API_PII_IMAGE_DESCRIPTION_MODEL = "api_pii_image_description_model",
  API_EXTERNAL_API_LIMITS = "api_external_api_limits",
}

export interface IBuildGlobalSearchPageEntryProps {
  id: string;
  titleKey: string;
  gates: readonly EGlobalSearchGate[];
  target: IGlobalSearchTarget;
  subtitleKey?: string;
}

export interface IBuildGlobalSearchProfileTabEntryProps {
  tab: TProfileSettingsTabValue;
  titleKey: string;
  gates: readonly EGlobalSearchGate[];
}

export interface IBuildGlobalSearchOrganizationTabEntryProps {
  tab: TOrganizationSettingsTabValue;
  titleKey: string;
  gates: readonly EGlobalSearchGate[];
}

export interface IGlobalSearchSettingOptionDefinition {
  // NOTE: A GLOBAL_SEARCH_SETTING_IDS value.
  setting: string;
  titleKey: string;
  // NOTE: Only the control's OWN rendering condition — the tab's gates are
  // added for it.
  gates?: readonly EGlobalSearchGate[];
}

export interface IBuildGlobalSearchSettingOptionEntryProps extends IGlobalSearchSettingOptionDefinition {
  // NOTE: The SETTINGS_TAB entry the control lives on. The option inherits its
  // target and gates, and shows its title as the subtitle.
  tabEntry: IGlobalSearchStaticEntry;
}

export interface IGlobalSearchTabOptionsProps {
  tabEntry: IGlobalSearchStaticEntry;
  options: readonly IGlobalSearchSettingOptionDefinition[];
}
